/**
 * Multi-ship fetch and overlay merge.
 *
 * **Fetch** fans out: one `POST /plot` per ship, in parallel. The backend takes
 * a single `ship_id`, and the parts that must be per-ship anyway - outlier
 * bounds computed from that ship's own data, curve fits, the "no data in this
 * month range" warning - come out right for free. A ship that fails is reported
 * on its own rather than sinking the whole plot.
 *
 * **Merge** is the interesting half. uPlot wants `AlignedData`: one x array and
 * N y arrays indexed against it. Two ships share no timestamps - they sample at
 * different instants, and LTTB then picks different points again - so the
 * overlay builds the sorted union of every ship's x and places each ship's
 * values at their own x, leaving `null` everywhere else.
 *
 * Nothing is interpolated. Resampling ships onto a common grid would invent
 * telemetry readings that were never recorded, which is the wrong trade for a
 * tool people use to answer questions about what a vessel actually did. The
 * gaps are real gaps, and the existing gap machinery already draws them.
 */

import { runPlot } from '../../api/endpoints.ts';
import { buildPlotRequest, type PlotRequestOpts } from './plot-request.ts';
import { liveSignalsForShip } from '../ship/ship-columns.ts';
import { DEFAULT_SOURCE } from '../../state/data-sources.ts';
import { shipLabel } from '../../utils/ships.ts';
import { tableColumns } from './table-config.ts';
import type {
  GaugeResponse,
  AnyPlotResponse,
  BoxResponse,
  TableMeasure,
  TableResponse,
  DerivedSignalReq,
  FitSpec,
  PlotRequest,
  PlotResponse,
  PlotSeries,
} from '../../api/types.ts';
import { userMessage } from '../../api/errors.ts';

/** One ship's slice of a multi-ship plot. */
export interface ShipPlotResult {
  ship: string;
  /** Null when the request failed; `error` says why. */
  data: AnyPlotResponse | null;
  error?: string;
}

/**
 * Legend/tooltip label for a series once more than one ship is plotted.
 *
 * Uses the ship's public name - the code is an identifier for the data path,
 * not something to read off a chart.
 */
export function shipSeriesLabel(ship: string, column: string): string {
  return `${shipLabel(ship)} · ${column}`;
}

/**
 * Narrow one ship's request to the signals that ship can actually draw.
 *
 * Ships do not share a schema, and the backend rejects a request naming a
 * column the ship lacks. Sent unscoped, a single ship-specific signal in the Y
 * list - `DG5_Power`, which P-ST has and A-CS does not - would 400 A-CS's whole
 * request and drop it from the chart along with every signal it *does* have.
 *
 * Returns `dropped` so the caller can say which signals were left out rather
 * than quietly drawing fewer lines than were asked for.
 */
export function scopeRequestToShip(
  body: PlotRequest,
  ship: string,
): { body: PlotRequest; dropped: string[]; skip?: string; tableKept?: number[] } {
  // Checked against the columns of the source this request reads, which for a
  // dashboard tile need not be the one Explore is on.
  const live = liveSignalsForShip(ship, body.source ?? DEFAULT_SOURCE);
  // No column list cached for this ship (schema fetch failed): send it as-is
  // and let the backend be the judge, rather than filtering everything away.
  if (!live.size) return { body, dropped: [] };

  const next = { ...body } as PlotRequest & Record<string, unknown>;
  const dropped: string[] = [];
  const keep = (names: string[] | undefined): string[] =>
    (names ?? []).filter((n) => {
      if (live.has(n)) return true;
      dropped.push(n);
      return false;
    });

  if ('x_column' in next && typeof next.x_column === 'string') {
    // The x column is structural - without it there is nothing to plot against.
    if (!live.has(next.x_column)) {
      return { body, dropped: [], skip: `no ${next.x_column}` };
    }
  }
  if ('y_left' in next) next.y_left = keep(next.y_left as string[]);
  if ('y_right' in next) next.y_right = keep(next.y_right as string[]);
  if ('hist_columns' in next) next.hist_columns = keep(next.hist_columns as string[]);
  if ('corr_columns' in next) next.corr_columns = keep(next.corr_columns as string[]);
  if ('pie_columns' in next) next.pie_columns = keep(next.pie_columns as string[]);
  if ('box_columns' in next) next.box_columns = keep(next.box_columns as string[]);
  for (const key of ['box_column', 'box_group_column'] as const) {
    const col = next[key];
    if (typeof col === 'string' && col && !live.has(col)) {
      return { body, dropped: [col], skip: `no ${col}` };
    }
  }
  if (typeof next.pie_band_column === 'string' && !live.has(next.pie_band_column)) {
    return { body, dropped: [next.pie_band_column], skip: `no ${next.pie_band_column}` };
  }
  if ('color_by' in next && typeof next.color_by === 'string' && !live.has(next.color_by)) {
    delete next.color_by;
  }
  // A table's rows need every group column; a missing one leaves the ship out.
  // A missing measure only blanks that column for this ship: it is not sent,
  // and `inflateTable` puts an empty column back so the ships still line up.
  let tableKept: number[] | undefined;
  if (next.plot_type === 'table') {
    // Readings: the server blanks a column this ship lacks, so nothing to do.
    if (next.table_mode === 'readings') return { body: next as PlotRequest, dropped };
    const missingGroup = tableColumns(next.table_groups ?? [], []).find((c) => !live.has(c));
    if (missingGroup) return { body, dropped: [missingGroup], skip: `no ${missingGroup}` };
    const kept: number[] = [];
    const measures = next.table_measures ?? [];
    measures.forEach((m, i) => {
      if (m.column && !live.has(m.column)) dropped.push(m.column);
      else kept.push(i);
    });
    if (!kept.length) return { body, dropped, skip: 'none of the selected signals' };
    if (kept.length < measures.length) {
      next.table_measures = kept.map((i) => measures[i]!);
      tableKept = kept;
    }
  }
  if (typeof next.number_column === 'string' && !live.has(next.number_column)) {
    return { body, dropped: [next.number_column], skip: `no ${next.number_column}` };
  }
  // Derived signals this ship cannot compute would fail validation on arrival.
  if (Array.isArray(next.derived)) {
    next.derived = (next.derived as DerivedSignalReq[]).filter((d) => live.has(d.name));
  }
  // Fits hang off series; drop the ones whose series went with them.
  if (Array.isArray(next.fits)) {
    const kept = new Set([
      ...((next.y_left as string[]) ?? []),
      ...((next.y_right as string[]) ?? []),
    ]);
    next.fits = (next.fits as FitSpec[]).filter((f) => kept.has(f.series));
  }

  const ySpec = 'y_left' in next || 'y_right' in next;
  const nLeft = ((next.y_left as string[]) ?? []).length;
  const nRight = ((next.y_right as string[]) ?? []).length;
  if (ySpec && nLeft + nRight === 0) {
    return { body, dropped, skip: 'none of the selected signals' };
  }
  if ('hist_columns' in next && ((next.hist_columns as string[]) ?? []).length === 0) {
    return { body, dropped, skip: 'none of the selected signals' };
  }
  // A matrix needs a pair: one signal left has nothing to correlate against.
  if ('corr_columns' in next && ((next.corr_columns as string[]) ?? []).length < 2) {
    return { body, dropped, skip: 'fewer than two of the selected signals' };
  }
  if ('box_columns' in next && next.box_mode !== 'groups' && ((next.box_columns as string[]) ?? []).length === 0) {
    return { body, dropped, skip: 'none of the selected signals' };
  }
  // A whole of one part is not a pie.
  if ('pie_columns' in next && ((next.pie_columns as string[]) ?? []).length < 2) {
    return { body, dropped, skip: 'fewer than two of the selected signals' };
  }
  return tableKept ? { body: next as PlotRequest, dropped, tableKept } : { body: next as PlotRequest, dropped };
}

/**
 * Put the columns a ship could not compute back into its table, empty, so its
 * rows line up with the other ships'. `kept[j]` is where the j-th returned
 * column belongs among `measures`.
 */
export function inflateTable(data: TableResponse, measures: TableMeasure[], kept: number[]): TableResponse {
  type Value = TableResponse['rows'][number]['values'][number];
  const spread = (values: Value[]): Value[] => {
    const out: Value[] = measures.map(() => null);
    kept.forEach((to, from) => (out[to] = values[from] ?? null));
    return out;
  };
  return {
    ...data,
    measures,
    rows: data.rows.map((r) => ({ ...r, values: spread(r.values) })),
    total: data.total ? { ...data.total, values: spread(data.total.values) } : null,
  };
}

/**
 * One `/plot` per ship, in parallel. Never rejects: a ship that errors comes
 * back with `data: null` so the caller can draw the rest and report the gap.
 */
export async function fetchShipPlots(
  shipIds: string[],
  opts?: PlotRequestOpts,
  refresh = false,
): Promise<ShipPlotResult[]> {
  return Promise.all(
    shipIds.map(async (ship): Promise<ShipPlotResult> => {
      try {
        const built = buildPlotRequest({ ...opts, ship });
        const scoped = scopeRequestToShip(built.body, ship);
        if (scoped.skip) return { ship, data: null, error: scoped.skip };
        let data = await runPlot(scoped.body, refresh);
        if (scoped.tableKept && data.plot_type === 'table' && built.body.plot_type === 'table') {
          data = inflateTable(data, built.body.table_measures ?? [], scoped.tableKept);
        }
        if (scoped.dropped.length) {
          const note = `no ${scoped.dropped.join(', ')}`;
          data.warning = data.warning ? `${data.warning} ${note}` : note;
        }
        return { ship, data };
      } catch (e) {
        return { ship, data: null, error: userMessage(e) };
      }
    }),
  );
}

/** Line/scatter results that actually carry points, in ship order. */
function usableXY(results: ShipPlotResult[]): Array<{ ship: string; data: PlotResponse }> {
  const out: Array<{ ship: string; data: PlotResponse }> = [];
  for (const r of results) {
    const d = r.data;
    if (!d) continue;
    if (d.plot_type !== 'line' && d.plot_type !== 'scatter') continue;
    if (!d.x?.length || !d.series?.length) continue;
    out.push({ ship: r.ship, data: d });
  }
  return out;
}

/**
 * The shared x axis, plus where each ship's point i lands on it.
 *
 * `collapse` decides what happens to points sharing an x value:
 *
 * - **Line** collapses them into one row. Two ships sampling the same instant
 *   belong on the same row, and a line cannot hold two y values at one x.
 * - **Scatter must not.** Its x is a measured column, not a timestamp - plot
 *   `total_power` against `SOG` over six months and the same SOG recurs
 *   thousands of times. Collapsing merged every one of those readings into a
 *   single slot where the last write won, silently discarding most of the
 *   cloud: 170k points would draw as a few thousand.
 *
 * Uncollapsed, the axis is every point from every ship, sorted, duplicates
 * kept - so `x.length` is the real total. uPlot only requires it ascending.
 */
function sharedX(perShip: number[][], collapse: boolean): { x: number[]; slots: number[][] } {
  const entries: Array<{ x: number; s: number; i: number }> = [];
  for (let s = 0; s < perShip.length; s++) {
    const xs = perShip[s]!;
    for (let i = 0; i < xs.length; i++) {
      const v = xs[i]!;
      if (Number.isFinite(v)) entries.push({ x: v, s, i });
    }
  }
  entries.sort((a, b) => a.x - b.x);

  const x: number[] = [];
  const slots: number[][] = perShip.map((xs) => new Array<number>(xs.length).fill(-1));
  for (const e of entries) {
    // Reuse the previous row only when collapsing and the x is identical.
    if (!(collapse && x.length && x[x.length - 1] === e.x)) x.push(e.x);
    slots[e.s]![e.i] = x.length - 1;
  }
  return { x, slots };
}

/**
 * Merge per-ship line/scatter responses into the single shared-x response the
 * renderers expect. Returns null when no ship produced points.
 */
export function mergeOverlay(results: ShipPlotResult[]): PlotResponse | null {
  const usable = usableXY(results);
  if (!usable.length) return null;
  // One ship: hand back its response untouched so the single-ship path stays
  // byte-identical to what it was before multi-ship existed.
  if (usable.length === 1 && results.length === 1) return usable[0]!.data;

  // Scatter keeps every point; line folds identical timestamps together.
  const { x, slots } = sharedX(
    usable.map((u) => u.data.x),
    usable[0]!.data.plot_type !== 'scatter',
  );
  const series: PlotSeries[] = [];
  const nullCounts: Record<string, number> = {};
  const fits: PlotResponse['fits'] = [];
  const warnings: string[] = [];
  let sourceRows = 0;

  usable.forEach(({ ship, data }, shipIdx) => {
    sourceRows += data.source_rows ?? 0;
    if (data.warning) warnings.push(`${shipLabel(ship)}: ${data.warning}`);

    // Where each of this ship's points lands on the shared axis.
    const slot = slots[shipIdx]!;

    for (const ser of data.series) {
      const values: Array<number | null> = new Array(x.length).fill(null);
      for (let i = 0; i < slot.length; i++) {
        const at = slot[i]!;
        if (at >= 0) values[at] = ser.values[i] ?? null;
      }
      series.push({
        name: shipSeriesLabel(ship, ser.name),
        values,
        axis: ser.axis,
        ship,
        column: ser.name,
      });
    }

    for (const [col, n] of Object.entries(data.null_counts ?? {})) {
      nullCounts[shipSeriesLabel(ship, col)] = n;
    }

    // Fits are computed per ship on that ship's own downsampled points, so they
    // are re-hung on the union the same way their series are.
    for (const fit of data.fits ?? []) {
      const next = { ...fit, series: shipSeriesLabel(ship, fit.series) };
      const fy = fit.y;
      if (Array.isArray(fy)) {
        const y: Array<number | null> = new Array(x.length).fill(null);
        for (let i = 0; i < slot.length; i++) {
          const at = slot[i]!;
          if (at >= 0) y[at] = fy[i] ?? null;
        }
        next.y = y;
      }
      fits.push(next);
    }
  });

  const first = usable[0]!.data;
  const failed = results.filter((r) => !r.data);
  for (const f of failed) warnings.push(`${shipLabel(f.ship)}: ${f.error ?? 'no data'}`);

  const merged: PlotResponse = {
    plot_type: first.plot_type,
    x,
    x_column: first.x_column,
    x_is_time: first.x_is_time,
    series,
    source_rows: sourceRows,
    returned_points: x.length,
    first_x: x.length ? x[0]! : null,
    last_x: x.length ? x[x.length - 1]! : null,
    null_counts: nullCounts,
    // Colour-by paints points from a third column, which collides with the
    // multi-ship colour modes. Dropped for overlays; grid keeps it per panel.
    color_by: null,
    color_values: null,
    color_categorical: false,
    fits,
  };
  if (warnings.length) merged.warning = warnings.join(' · ');
  if (first.filter_summary != null) merged.filter_summary = first.filter_summary;
  return merged;
}

/**
 * True when this plot type can be overlaid across ships today.
 *
 * Histogram needs bin edges computed across every ship at once - the response
 * carries counts, not the underlying values, so the client cannot re-bin - and
 * that needs the backend to accept a ship list. Map draws through Leaflet
 * rather than uPlot and needs its own multi-track path.
 */
export function supportsOverlay(plotType: string): boolean {
  return (
    plotType === 'line' || plotType === 'scatter' || plotType === 'box' || plotType === 'table' || plotType === 'gauge'
  );
}

/**
 * Several ships' tables as one: each ship's rows tagged with it, one after
 * another, and each ship's own total kept - a total across ships would need the
 * rows, not the summaries, and a mean of means is not a mean.
 */
export function mergeTableOverlay(results: ShipPlotResult[]): TableResponse | null {
  const usable = results.filter(
    (r): r is ShipPlotResult & { data: TableResponse } => r.data?.plot_type === 'table' && !!r.data.rows?.length,
  );
  if (!usable.length) return null;
  if (usable.length === 1 && results.length === 1) return usable[0]!.data;
  const first = usable[0]!.data;
  const warnings: string[] = [];
  for (const r of results) {
    if (!r.data) warnings.push(`${shipLabel(r.ship)}: ${r.error ?? 'no data'}`);
    else if (r.data.warning) warnings.push(`${shipLabel(r.ship)}: ${r.data.warning}`);
  }
  return {
    ...first,
    rows: usable.flatMap(({ ship, data }) => data.rows.map((row) => ({ ...row, ship }))),
    total: null,
    totals: usable.flatMap(({ ship, data }) => (data.total ? [{ ship, ...data.total }] : [])),
    rows_left_out: usable.reduce((n, u) => n + (u.data.rows_left_out ?? 0), 0),
    // Every ship is asked for the same page, so the pages run out with the
    // ship that has the most readings.
    max_ship_rows: Math.max(...usable.map((u) => u.data.source_rows ?? 0)),
    source_rows: usable.reduce((n, u) => n + (u.data.source_rows ?? 0), 0),
    warning: warnings.length ? warnings.join(' · ') : null,
  };
}

/**
 * Several ships' gauges as one: each ship's dials kept whole, in the order the
 * ships were picked, and a ship that failed kept too - as a row that says so -
 * so a dashboard of six ships never silently shows five. The one canvas lays
 * them out (by ship, by dial, or as a matrix); nothing is joined.
 */
export function mergeGaugeOverlay(results: ShipPlotResult[]): GaugeResponse | null {
  const usable = results.filter(
    (r): r is ShipPlotResult & { data: GaugeResponse } => r.data?.plot_type === 'gauge',
  );
  if (!usable.length) return null;
  if (usable.length === 1 && results.length === 1) return usable[0]!.data;
  const first = usable[0]!.data;
  const warnings: string[] = [];
  for (const r of results) {
    if (r.data?.warning) warnings.push(`${shipLabel(r.ship)}: ${r.data.warning}`);
  }
  return {
    ...first,
    ships: results.map((r) =>
      r.data?.plot_type === 'gauge'
        ? { ship: r.ship, dials: r.data.dials, ...(r.data.readings ? { readings: r.data.readings } : {}) }
        : { ship: r.ship, dials: null, error: r.error ?? 'No data' },
    ),
    source_rows: usable.reduce((n, u) => n + (u.data.source_rows ?? 0), 0),
    warning: warnings.length ? warnings.join(' · ') : null,
  };
}

/**
 * Several ships' box plots as one: every box kept, tagged with its ship, so
 * the renderer can set each category's ships side by side. Boxes are summaries
 * - nothing to align on an axis - so unlike `mergeOverlay` this is a
 * concatenation, not a join.
 */
export function mergeBoxOverlay(results: ShipPlotResult[]): BoxResponse | null {
  const usable = results.filter(
    (r): r is ShipPlotResult & { data: BoxResponse } =>
      r.data?.plot_type === 'box' && !!r.data.boxes?.length,
  );
  if (!usable.length) return null;
  if (usable.length === 1 && results.length === 1) return usable[0]!.data;
  const first = usable[0]!.data;
  const warnings: string[] = [];
  for (const r of results) {
    if (!r.data) warnings.push(`${shipLabel(r.ship)}: ${r.error ?? 'no data'}`);
    else if (r.data.warning) warnings.push(`${shipLabel(r.ship)}: ${r.data.warning}`);
  }
  const merged: BoxResponse = {
    ...first,
    boxes: usable.flatMap(({ ship, data }) => data.boxes.map((b) => ({ ...b, ship }))),
    excluded: [],
    groups_left_out: Math.max(...usable.map((u) => u.data.groups_left_out ?? 0)),
    source_rows: usable.reduce((n, u) => n + (u.data.source_rows ?? 0), 0),
    warning: warnings.length ? warnings.join(' · ') : null,
  };
  return merged;
}

/**
 * True when several ships can be drawn as one panel per ship.
 *
 * Wider than `supportsOverlay`: a heatmap is a density field, and two of them
 * stacked are unreadable whatever the opacity - but side by side they are
 * exactly the comparison people want. So heatmap is grid-only rather than
 * single-ship-only, and the fan-out runs for it even though the merge cannot.
 * A correlation matrix is the same shape of thing: two matrices cannot share
 * cells, and "does this pair behave the same on both ships" is read across
 * panels. So is a pie: two sets of slices cannot share one circle - and a
 * Sankey, whose boxes and ribbons are one ship's flows.
 */
export function supportsGrid(plotType: string): boolean {
  return (
    supportsOverlay(plotType) ||
    plotType === 'heatmap' ||
    plotType === 'correlation' ||
    plotType === 'pie' ||
    plotType === 'sankey' ||
    plotType === 'number'
  );
}

/**
 * Whether the effective multi-ship layout is one panel per ship.
 *
 * Grid-only plot types use panels even when the saved preference still says
 * overlay. Controls that allocate colours between ships must follow the
 * effective layout: a panel title already identifies its one ship.
 */
export function usesGridPanels(
  plotType: string,
  viewMode: 'overlay' | 'grid',
): boolean {
  return supportsGrid(plotType) && (viewMode === 'grid' || !supportsOverlay(plotType));
}

/**
 * Lines past which an overlay stops being readable. Six ships carrying six
 * signals each is 36 lines; the chart still draws, but the user is told and
 * pointed at grid view rather than left to wonder.
 */
export const OVERLAY_LINE_WARN = 15;

/** Crowding note for an overlay, or null while it is still legible. */
export function overlayCrowdingWarning(
  seriesCount: number,
  shipCount: number,
): string | null {
  if (seriesCount <= OVERLAY_LINE_WARN) return null;
  return (
    `${seriesCount} lines across ${shipCount} ships - ` +
    'grid view keeps them readable.'
  );
}
